import { OpenInFull } from '@mui/icons-material';
import { Skeleton, ThemeProvider } from '@mui/material';
import { IAssemblyScreen, ISection } from 'gcf-player/shared-utils';
import { first, last, omit, set } from 'lodash';
import {
  lazy,
  memo,
  Suspense,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DEFAULT_SCREEN_ALL, EVENT_TYPES } from 'src/constants';
import { useEventListener, useWindow } from 'src/hooks';
import { useCleanUpDefaultFonts } from 'src/hooks/useCleanUpDefaultFonts';
import { ProductClickEvent, SectionClickEvent } from 'src/interfaces/Events';
import { lang, theme } from 'src/utils';

import { DynamicTooltipChip } from '../dynamicTooltipChip/DynamicTooltipChip';
import { GCFErrorBoundary } from '../errors/GCFErrorBoundary';
import {
  filterOutDynamicSection,
  getActiveScreenFromScreens,
  getComponentInstances,
  getFilteredScreenByPosition,
  getScreenSxValue,
  getSectionComponentMetadata,
  getSectionDimmedState,
  getZoomValueByContainerSize,
  isExpandScreenButtonVisible,
} from './GcfPreview.helper';
import {
  IGcfPreview,
  IPreviewSectionContentProps,
  IPreviewSectionsProps,
  SelectedSection,
} from './GcfPreview.interface';
import {
  PreviewWrapper,
  ScreenContainer,
  ScreenLabelButton,
  ScreenScaleLayer,
  ScreenWrapper,
  SectionContainer,
} from './GcfPreview.styled';
import { SectionPill } from './modules/sectionPill';

const SCREEN_CHIP_HEIGHT = 48;

const ConfigProvider = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.ConfigProvider,
  })),
);

const Section = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.Section,
  })),
);

const PreviewSectionContent = ({
  section,
  screenKey,
  screenWidth,
  screenHeight,
  activePosition,
  clickable,
}: IPreviewSectionContentProps) => {
  const updatedAssemblyComponents = (section?.components ?? []).map(
    (data: any) => {
      if (!data?.component?.theme) {
        set(
          data,
          'component.theme',
          data.component?.config?.scaleTypeKey ??
            data.component?.config?.themeKey ??
            'default',
        );
      }

      return data;
    },
  );

  return (
    <Section
      {...omit(section, 'uid')}
      screenWidth={screenWidth}
      screenHeight={screenHeight}
      assemblyComponents={updatedAssemblyComponents}
      left="0"
      top="0"
      screenSequence={screenKey}
      activePosition={activePosition}
      clickable={clickable}
    />
  );
};

const PreviewSections = memo(
  ({
    screen,
    screenKey,
    screenWidth,
    screenHeight,
    previewData,
    activePosition,
    selectedSection,
    highlightAll,
    clickable,
    showSectionPill,
    previewZoom,
  }: IPreviewSectionsProps) => {
    const filteredScreen = useMemo(
      () => getFilteredScreenByPosition(screen, activePosition),
      [screen, activePosition],
    );

    return (
      <>
        {filterOutDynamicSection(filteredScreen?.sections)?.map((section) => {
          const { componentData, allowOverride } = getSectionComponentMetadata(
            section,
            previewData,
          );

          const isDimmed = getSectionDimmedState({
            section,
            screenSequence: screenKey,
            selectedSection,
            allowOverride,
            highlightAll,
          });

          const renderKey = section.id === 0 ? section.uid : section.id;

          return (
            <SectionContainer
              key={renderKey}
              data-testid="section-with-pill-container"
              $dimmed={isDimmed}
              sx={{
                top: section.top,
                left: section.left,
                width: componentData?.width,
                height: componentData?.height,
              }}>
              <PreviewSectionContent
                section={section}
                screenKey={screenKey}
                screenWidth={screenWidth}
                screenHeight={screenHeight}
                activePosition={activePosition}
                clickable={clickable}
              />
              {showSectionPill && (
                <ThemeProvider theme={theme}>
                  <SectionPill section={section} zoom={previewZoom} />
                </ThemeProvider>
              )}
            </SectionContainer>
          );
        })}
      </>
    );
  },
);

export const GcfPreview = (props: IGcfPreview) => {
  const {
    previewData,
    activePosition = 1,
    activeScreen = DEFAULT_SCREEN_ALL,
    highlightAll = true,
    showSectionPill = true,
    selectedSection,
    minScreenSize,
    signals,
    onClick,
    onScreenLabelClick,
  } = props;

  useCleanUpDefaultFonts();

  const [mockContainerRef, setMockContainerRef] = useState<{
    current: HTMLDivElement | null;
  }>({
    current: null,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerWidth, containerHeight, isOverflowX, isOverflowY } =
    useWindow(mockContainerRef);

  const allAssemblyScreens = previewData?.activeAssembly?.screens ?? {};
  const visibleAssemblyScreens = getActiveScreenFromScreens(
    activeScreen,
    allAssemblyScreens,
  );
  const visibleScreenSequenceSet = useMemo(
    () => new Set(Object.keys(visibleAssemblyScreens)),
    [visibleAssemblyScreens],
  );
  const visibleScreenSequenceList = useMemo(
    () => Object.keys(visibleAssemblyScreens),
    [visibleAssemblyScreens],
  );
  const isClickable = !!onClick;
  const isSingleScreenView = String(activeScreen) !== DEFAULT_SCREEN_ALL;
  const showExpandScreenButton = isExpandScreenButtonVisible(
    onScreenLabelClick,
    isSingleScreenView,
  );
  const previewZoom = getZoomValueByContainerSize(
    {
      width: containerWidth,
      height: containerHeight - SCREEN_CHIP_HEIGHT,
    },
    visibleAssemblyScreens,
    previewData?.ccData.screens,
    minScreenSize,
  );

  const handleSectionClick = (event: CustomEvent<SectionClickEvent>) => {
    if (!onClick) {
      return;
    }

    const {
      position,
      screenSequence,
      id,
      componentId,
      isScheduledItem,
      parent,
    } = event.detail;
    let selectedSection: SelectedSection | undefined = undefined;

    if (id && position && screenSequence) {
      const selectedSectionInfo = allAssemblyScreens[
        screenSequence
      ].sections.find((section: ISection) => section.id === id) as ISection;

      selectedSection = {
        id,
        position,
        screenSequence,
        left: selectedSectionInfo?.left ?? 0,
        top: selectedSectionInfo?.top ?? 0,
      };
    }

    const instances = getComponentInstances(
      {
        componentId: Number(componentId),
        position,
        screenSequence,
        id,
        isScheduledItem: !!isScheduledItem,
        parent,
      },
      previewData,
    );

    onClick(instances, selectedSection);
  };

  const handleProductClick = (event: CustomEvent<ProductClickEvent>) => {
    /* TODO: Handle product clicks, as they are currently identical to section clicks. */
    if (!onClick) {
      return;
    }

    const {
      position,
      screenSequence,
      id,
      componentId,
      isScheduledItem,
      parent,
    } = event.detail;
    let selectedSection: SelectedSection | undefined = undefined;

    if (id && position && screenSequence) {
      const selectedSectionInfo = allAssemblyScreens[
        screenSequence
      ].sections.find((section: ISection) => section.id === id) as ISection;

      selectedSection = {
        id,
        position,
        screenSequence,
        left: selectedSectionInfo?.left ?? 0,
        top: selectedSectionInfo?.top ?? 0,
      };
    }

    const instances = getComponentInstances(
      {
        componentId: Number(componentId),
        position,
        screenSequence,
        id,
        isScheduledItem: !!isScheduledItem,
        parent,
      },
      previewData,
    );

    onClick(instances, selectedSection);
  };

  useEventListener(
    EVENT_TYPES.SECTION_CLICK,
    handleSectionClick,
    isClickable ? containerRef : null,
  );
  useEventListener(
    EVENT_TYPES.PRODUCT_CLICK,
    handleProductClick,
    isClickable ? containerRef : null,
  );

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setMockContainerRef({
        current: containerRef?.current,
      });
    }
  }, [activeScreen]);

  const resolvedActivePosition = signals?.activePosition ?? activePosition;

  const configProviderProps = useMemo(
    () => ({
      products: previewData?.mappedProducts,
      components: previewData?.ccData?.components,
      projectConfig: previewData?.projectConfig,
      themes: previewData?.ccData?.themes,
      assemblyConfig: previewData?.activeAssembly?.config,
      allScreens: previewData?.activeAssembly?.screens,
    }),
    [previewData],
  );

  return previewData ? (
    <PreviewWrapper
      ref={containerRef}
      data-testid="preview-wrapper"
      $isOverflowX={isOverflowX}
      $isOverflowY={isOverflowY}>
      {Object.entries<IAssemblyScreen>(allAssemblyScreens)?.map(
        ([key, screen]: [string, IAssemblyScreen]) => {
          const isVisibleScreen = visibleScreenSequenceSet.has(key);
          const isFirstVisibleScreen = first(visibleScreenSequenceList) === key;
          const isLastVisibleScreen = last(visibleScreenSequenceList) === key;
          const hasRightBorder = isSingleScreenView || isLastVisibleScreen;
          const { width, height } =
            previewData?.ccData?.screens?.[screen?.screenId] ?? {};
          const renderPreviewSections = (position: number | string) => (
            <PreviewSections
              screen={screen}
              screenKey={key}
              screenWidth={width}
              screenHeight={height}
              previewData={previewData}
              activePosition={position}
              selectedSection={selectedSection}
              highlightAll={highlightAll}
              clickable={isClickable}
              showSectionPill={showSectionPill}
              previewZoom={previewZoom}
            />
          );

          return (
            <ScreenContainer key={key} sx={getScreenSxValue(isVisibleScreen)}>
              {showExpandScreenButton ? (
                <ScreenLabelButton
                  variant="contained-secondary"
                  size="small"
                  onClick={() => onScreenLabelClick?.(key)}
                  endIcon={<OpenInFull />}>
                  {`${lang.screen} ${key}`}
                </ScreenLabelButton>
              ) : !onScreenLabelClick ? (
                <DynamicTooltipChip
                  chipName={`${lang.screen} ${key}`}
                  sx={{ marginBottom: '16px' }}
                />
              ) : null}
              <ScreenWrapper
                $width={width}
                $height={height}
                $zoom={previewZoom}
                $hasRightBorder={hasRightBorder}
                $isFirstVisible={isFirstVisibleScreen}>
                <ScreenScaleLayer
                  $width={width}
                  $height={height}
                  $zoom={previewZoom}
                  $hasRightBorder={hasRightBorder}>
                  <GCFErrorBoundary>
                    <Suspense
                      fallback={
                        <Skeleton
                          sx={{ bgcolor: 'grey.700' }}
                          height="100%"
                          width="100%"
                          variant="rectangular"
                        />
                      }>
                      <ConfigProvider isPreview {...configProviderProps}>
                        {renderPreviewSections(resolvedActivePosition)}
                      </ConfigProvider>
                    </Suspense>
                  </GCFErrorBoundary>
                </ScreenScaleLayer>
              </ScreenWrapper>
            </ScreenContainer>
          );
        },
      )}
    </PreviewWrapper>
  ) : null;
};
